<script lang="ts" setup>
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useFormat } from '@/composables/format';
import { atcb_action, type ATCBActionEventConfig } from 'add-to-calendar-button';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ButtonVariant } from '../../../../models/button-variant.enum';
import type { ResolvedSelfSellInModel } from '../../composables/useSelfSellIn';
import InfoCardContainer from '../InfoCardContainer.vue';

const props = defineProps<{
  data: NonNullable<ResolvedSelfSellInModel['importantDatesSection']>['events'][number];
}>();

const { t } = useI18n();
const { formatDate } = useFormat();

const formattedDate = computed(() =>
  formatDate(props.data.date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }),
);

const resolvedTitle = computed(() => props.data.title);
const resolvedDescription = computed(() => props.data.description ?? '');

const calendarDescription = computed(() => {
  return resolvedDescription.value ? `[p]${resolvedDescription.value?.replace(/\n/gi, ' ')}[/p]` : '';
});

function handleAddToCalendar() {
  const config: ATCBActionEventConfig = {
    name: resolvedTitle.value,
    description: calendarDescription.value,
    startDate: props.data.date,
    iCalFileName: resolvedTitle.value,
  };

  atcb_action(config);
}
</script>

<template>
  <InfoCardContainer>
    <div class="important-dates-card-header">
      <p class="important-dates-card-header__date">{{ formattedDate }}</p>
      <h3 class="important-dates-card-header__title">{{ resolvedTitle }}</h3>
    </div>
    <p v-if="resolvedDescription" class="important-dates-card-details">{{ resolvedDescription }}</p>
    <BackstageButton
      class="important-dates-card-button"
      :variant="ButtonVariant.PRIMARY_INVERTED"
      size="small"
      @click="handleAddToCalendar"
      >{{ t('viewSelfSellIn.important_dates.add_to_calendar') }}</BackstageButton
    >
  </InfoCardContainer>
</template>

<style lang="scss" scoped>
.important-dates-card-button {
  margin-top: auto;
  width: fit-content;
}

.important-dates-card-header {
  display: flex;
  flex-direction: column;

  &__date {
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 130%;
    opacity: 0.5;
    margin: 0;
  }

  &__title {
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1.3;
    margin: 0;
  }
}

.important-dates-card-details {
  white-space: pre-line;
  margin: 0;
  font-size: 1rem;
  line-height: 1.3;
  font-weight: 400;
}
</style>
